import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Plus, Star, MoreHorizontal, Trash2, Edit, Calendar, Clock, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { format } from 'date-fns';
import { labelColors } from '../mockData';
import { boardAPI, listAPI, cardAPI, handleAPIError } from '../services/api';
import { toast } from 'sonner';

const Board = ({ board, onUpdateBoard }) => {
  const [boardData, setBoardData] = useState(board);
  const [newListTitle, setNewListTitle] = useState('');
  const [isAddingList, setIsAddingList] = useState(false);
  const [newCardTitles, setNewCardTitles] = useState({});
  const [isAddingCard, setIsAddingCard] = useState({});
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);
  const [dueDateCard, setDueDateCard] = useState(null);
  const [dueDateList, setDueDateList] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setBoardData(board);
  }, [board]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    try {
      if (type === 'list') {
        const newLists = Array.from(boardData.lists);
        const [reorderedList] = newLists.splice(source.index, 1);
        newLists.splice(destination.index, 0, reorderedList);
        
        // Update positions
        newLists.forEach((list, index) => {
          list.position = index;
        });
        
        const updatedBoard = { ...boardData, lists: newLists };
        setBoardData(updatedBoard);
        
        // Update backend
        await boardAPI.reorderBoard(boardData.id, updatedBoard);
        onUpdateBoard(updatedBoard);
        return;
      }

      const sourceList = boardData.lists.find(list => list.id === source.droppableId);
      const destinationList = boardData.lists.find(list => list.id === destination.droppableId);

      if (source.droppableId === destination.droppableId) {
        // Same list reordering
        const newCards = Array.from(sourceList.cards);
        const [reorderedCard] = newCards.splice(source.index, 1);
        newCards.splice(destination.index, 0, reorderedCard);
        
        // Update positions
        newCards.forEach((card, index) => {
          card.position = index;
        });
        
        const updatedLists = boardData.lists.map(list => 
          list.id === sourceList.id ? { ...list, cards: newCards } : list
        );
        
        const updatedBoard = { ...boardData, lists: updatedLists };
        setBoardData(updatedBoard);
        
        // Update backend
        await boardAPI.reorderBoard(boardData.id, updatedBoard);
        onUpdateBoard(updatedBoard);
      } else {
        // Moving between lists
        const sourceCards = Array.from(sourceList.cards);
        const destinationCards = Array.from(destinationList.cards);
        const [movedCard] = sourceCards.splice(source.index, 1);
        
        movedCard.list_id = destination.droppableId;
        movedCard.position = destination.index;
        destinationCards.splice(destination.index, 0, movedCard);
        
        // Update positions in both lists
        sourceCards.forEach((card, index) => {
          card.position = index;
        });
        destinationCards.forEach((card, index) => {
          card.position = index;
        });
        
        const updatedLists = boardData.lists.map(list => {
          if (list.id === sourceList.id) return { ...list, cards: sourceCards };
          if (list.id === destinationList.id) return { ...list, cards: destinationCards };
          return list;
        });
        
        const updatedBoard = { ...boardData, lists: updatedLists };
        setBoardData(updatedBoard);
        
        // Update backend
        await boardAPI.reorderBoard(boardData.id, updatedBoard);
        onUpdateBoard(updatedBoard);
      }
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to move item: ${errorInfo.message}`);
      console.error('Error in drag and drop:', error);
      
      // Revert to original state
      setBoardData(board);
    }
  };

  const addList = async () => {
    if (!newListTitle.trim()) return;
    
    try {
      const newList = await listAPI.createList(boardData.id, {
        title: newListTitle
      });
      
      const updatedBoard = {
        ...boardData,
        lists: [...boardData.lists, newList]
      };
      
      setBoardData(updatedBoard);
      onUpdateBoard(updatedBoard);
      setNewListTitle('');
      setIsAddingList(false);
      toast.success(`List "${newList.title}" created successfully!`);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to create list: ${errorInfo.message}`);
      console.error('Error creating list:', error);
    }
  };

  const addCard = async (listId) => {
    const cardTitle = newCardTitles[listId];
    if (!cardTitle?.trim()) return;
    
    try {
      const newCard = await cardAPI.createCard(listId, {
        title: cardTitle,
        description: '',
        labels: [],
        members: []
      });
      
      const updatedLists = boardData.lists.map(list => 
        list.id === listId 
          ? { ...list, cards: [...list.cards, newCard] }
          : list
      );
      
      const updatedBoard = { ...boardData, lists: updatedLists };
      setBoardData(updatedBoard);
      onUpdateBoard(updatedBoard);
      setNewCardTitles({ ...newCardTitles, [listId]: '' });
      setIsAddingCard({ ...isAddingCard, [listId]: false });
      toast.success(`Card "${newCard.title}" created successfully!`);
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to create card: ${errorInfo.message}`);
      console.error('Error creating card:', error);
    }
  };

  const deleteCard = async (cardId) => {
    try {
      await cardAPI.deleteCard(cardId);
      
      // Remove card from the board state
      const updatedLists = boardData.lists.map(list => ({
        ...list,
        cards: list.cards.filter(card => card.id !== cardId)
      }));
      
      const updatedBoard = { ...boardData, lists: updatedLists };
      setBoardData(updatedBoard);
      onUpdateBoard(updatedBoard);
      setDeleteConfirmCard(null);
      toast.success('Card deleted successfully!');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to delete card: ${errorInfo.message}`);
      console.error('Error deleting card:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmCard) {
      await deleteCard(deleteConfirmCard.id);
    }
  };

  const setCardDueDate = async (cardId, dueDate) => {
    try {
      await cardAPI.updateCard(cardId, { due_date: dueDate });
      
      // Update card in the board state
      const updatedLists = boardData.lists.map(list => ({
        ...list,
        cards: list.cards.map(card => 
          card.id === cardId ? { ...card, due_date: dueDate } : card
        )
      }));
      
      const updatedBoard = { ...boardData, lists: updatedLists };
      setBoardData(updatedBoard);
      onUpdateBoard(updatedBoard);
      setDueDateCard(null);
      setSelectedDate(null);
      toast.success('Due date updated successfully!');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to update due date: ${errorInfo.message}`);
      console.error('Error updating due date:', error);
    }
  };

  const setListDueDate = async (listId, dueDate) => {
    try {
      await listAPI.updateList(listId, { due_date: dueDate });
      
      // Update list in the board state
      const updatedLists = boardData.lists.map(list => 
        list.id === listId ? { ...list, due_date: dueDate } : list
      );
      
      const updatedBoard = { ...boardData, lists: updatedLists };
      setBoardData(updatedBoard);
      onUpdateBoard(updatedBoard);
      setDueDateList(null);
      setSelectedDate(null);
      toast.success('List due date updated successfully!');
    } catch (error) {
      const errorInfo = handleAPIError(error);
      toast.error(`Failed to update list due date: ${errorInfo.message}`);
      console.error('Error updating list due date:', error);
    }
  };

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return null;
    
    const now = new Date();
    const due = new Date(dueDate);
    const timeDiff = due.getTime() - now.getTime();
    const daysDiff = timeDiff / (1000 * 3600 * 24);
    
    if (daysDiff < 0) {
      return 'overdue'; // Red dot
    } else if (daysDiff <= 1) {
      return 'due-soon'; // Yellow dot
    } else {
      return 'on-track'; // Green dot
    }
  };

  const getDueDateColor = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-500';
      case 'due-soon':
        return 'bg-yellow-500';
      case 'on-track':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return '';
    const date = new Date(dueDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const cardDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (cardDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (cardDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return format(date, 'MMM d');
    }
  };

  const getLabelColor = (label) => {
    const index = label.length % labelColors.length;
    return labelColors[index];
  };

  return (
    <div 
      className="min-h-screen p-4"
      style={{ backgroundColor: boardData.color || '#4FC3F7' }}
    >
      {/* Board Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-white text-2xl font-bold">{boardData.title}</h1>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
          >
            <Star className={`h-4 w-4 ${boardData.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
          </Button>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>Board settings</DropdownMenuItem>
            <DropdownMenuItem>Change background</DropdownMenuItem>
            <DropdownMenuItem>Archive board</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Board Lists */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="board" direction="horizontal" type="list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="flex space-x-4 overflow-x-auto pb-4"
            >
              {boardData.lists.map((list, index) => (
                <Draggable key={list.id} draggableId={list.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="bg-gray-100 rounded-lg p-3 w-72 flex-shrink-0"
                    >
                      {/* List Header */}
                      <div
                        {...provided.dragHandleProps}
                        className="mb-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-800">{list.title}</h3>
                            {list.due_date && (
                              <div className={`w-3 h-3 rounded-full ${getDueDateColor(getDueDateStatus(list.due_date))}`} 
                                   title={`List due: ${new Date(list.due_date).toLocaleDateString()}`}>
                              </div>
                            )}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="p-1 h-auto">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setIsAddingCard(prev => ({ ...prev, [list.id]: true }))}>
                                Add card
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setDueDateList(list);
                                setSelectedDate(list.due_date ? new Date(list.due_date) : null);
                              }}>
                                <Calendar className="h-4 w-4 mr-2" />
                                {list.due_date ? 'Edit due date' : 'Set due date'}
                              </DropdownMenuItem>
                              {list.due_date && (
                                <DropdownMenuItem onClick={() => setListDueDate(list.id, null)}>
                                  <X className="h-4 w-4 mr-2" />
                                  Remove due date
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem>Copy list</DropdownMenuItem>
                              <DropdownMenuItem>Archive list</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        {list.due_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            Due: {new Date(list.due_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      {/* List Cards */}
                      <Droppable droppableId={list.id} type="card">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="space-y-2 min-h-1"
                          >
                            {list.cards.map((card, cardIndex) => (
                              <Draggable key={card.id} draggableId={card.id} index={cardIndex}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <Card className="bg-white hover:bg-gray-50 cursor-pointer shadow-sm border-0 group">
                                      <CardContent className="p-3 relative">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                                              {card.title}
                                            </h4>
                                            {card.labels && card.labels.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mb-2">
                                                {card.labels.map((label, labelIndex) => (
                                                  <Badge
                                                    key={labelIndex}
                                                    style={{ backgroundColor: getLabelColor(label) }}
                                                    className="text-white text-xs border-0"
                                                  >
                                                    {label}
                                                  </Badge>
                                                ))}
                                              </div>
                                            )}
                                            {card.description && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                {card.description}
                                              </p>
                                            )}
                                            {/* Due Date and Members Row */}
                                            <div className="flex items-center justify-between mt-2">
                                              {/* Due Date Indicator */}
                                              {card.due_date && (
                                                <div className="flex items-center space-x-1">
                                                  <div className={`w-2 h-2 rounded-full ${getDueDateColor(getDueDateStatus(card.due_date))}`}></div>
                                                  <span className={`text-xs ${
                                                    getDueDateStatus(card.due_date) === 'overdue' 
                                                      ? 'text-red-600 font-medium' 
                                                      : getDueDateStatus(card.due_date) === 'due-soon'
                                                      ? 'text-yellow-600 font-medium'
                                                      : 'text-gray-600'
                                                  }`}>
                                                    <Clock className="w-3 h-3 inline mr-1" />
                                                    {formatDueDate(card.due_date)}
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Members */}
                                              {card.members && card.members.length > 0 && (
                                                <div className="flex -space-x-1">
                                                  {card.members.slice(0, 3).map((member, memberIndex) => (
                                                    <div
                                                      key={memberIndex}
                                                      className="w-6 h-6 bg-gray-400 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-medium"
                                                    >
                                                      {member.charAt(0).toUpperCase()}
                                                    </div>
                                                  ))}
                                                  {card.members.length > 3 && (
                                                    <div className="w-6 h-6 bg-gray-300 rounded-full border-2 border-white flex items-center justify-center text-xs text-gray-600">
                                                      +{card.members.length - 3}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <MoreHorizontal className="h-3 w-3" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                              <DropdownMenuItem>
                                                <Edit className="h-4 w-4 mr-2" />
                                                Edit card
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setDueDateCard(card);
                                                  setSelectedDate(card.due_date ? new Date(card.due_date) : null);
                                                }}
                                              >
                                                <Calendar className="h-4 w-4 mr-2" />
                                                {card.due_date ? 'Change due date' : 'Set due date'}
                                              </DropdownMenuItem>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuItem
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setDeleteConfirmCard(card);
                                                }}
                                                className="text-red-600 focus:text-red-600"
                                              >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Delete card
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>

                      {/* Add Card Button */}
                      {isAddingCard[list.id] ? (
                        <div className="mt-2">
                          <Input
                            value={newCardTitles[list.id] || ''}
                            onChange={(e) => setNewCardTitles({
                              ...newCardTitles,
                              [list.id]: e.target.value
                            })}
                            placeholder="Enter card title..."
                            className="mb-2"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') addCard(list.id);
                            }}
                            autoFocus
                          />
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => addCard(list.id)}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Add card
                            </Button>
                            <Button
                              onClick={() => setIsAddingCard({
                                ...isAddingCard,
                                [list.id]: false
                              })}
                              variant="ghost"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setIsAddingCard({
                            ...isAddingCard,
                            [list.id]: true
                          })}
                          variant="ghost"
                          className="w-full justify-start text-gray-600 hover:bg-gray-200 mt-2"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add a card
                        </Button>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              
              {/* Add List */}
              {isAddingList ? (
                <div className="bg-gray-100 rounded-lg p-3 w-72 flex-shrink-0">
                  <Input
                    value={newListTitle}
                    onChange={(e) => setNewListTitle(e.target.value)}
                    placeholder="Enter list title..."
                    className="mb-2"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') addList();
                    }}
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <Button
                      onClick={addList}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Add list
                    </Button>
                    <Button
                      onClick={() => setIsAddingList(false)}
                      variant="ghost"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setIsAddingList(true)}
                  variant="ghost"
                  className="bg-white/20 hover:bg-white/30 text-white w-72 flex-shrink-0 justify-start"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add another list
                </Button>
              )}
              
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Delete Card Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmCard} onOpenChange={() => setDeleteConfirmCard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirmCard?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Due Date Picker Dialog */}
      <Dialog open={!!dueDateCard} onOpenChange={() => setDueDateCard(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Due Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Card: "{dueDateCard?.title}"
            </div>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDueDateCard(null);
                  setSelectedDate(null);
                }}
              >
                Cancel
              </Button>
              {dueDateCard?.due_date && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCardDueDate(dueDateCard.id, null);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove Date
                </Button>
              )}
              <Button
                onClick={() => {
                  if (selectedDate && dueDateCard) {
                    setCardDueDate(dueDateCard.id, selectedDate.toISOString());
                  }
                }}
                disabled={!selectedDate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* List Due Date Picker Dialog */}
      <Dialog open={!!dueDateList} onOpenChange={() => setDueDateList(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set List Due Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              List: "{dueDateList?.title}"
            </div>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDueDateList(null);
                  setSelectedDate(null);
                }}
              >
                Cancel
              </Button>
              {dueDateList?.due_date && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setListDueDate(dueDateList.id, null);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove Date
                </Button>
              )}
              <Button
                onClick={() => {
                  if (selectedDate && dueDateList) {
                    setListDueDate(dueDateList.id, selectedDate.toISOString());
                  }
                }}
                disabled={!selectedDate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Board;